import { useState, useCallback, useEffect } from 'react';
import { useUser } from '@clerk/react';
import { Card, Button, Form, Alert, ButtonGroup } from 'react-bootstrap';
import {
  RouteAnalysisResponse,
  CyclingZone,
  StrategyOptions,
  FuelIntervalResponse,
} from '../../models';
import { calculateFuelIntervals } from '../../api/Api';
import { useLocaleContext } from '../../i18n';
import FtpSlider from '../sliders/FtpSlider';
import WeightSlider from '../sliders/WeightSlider';
import BikeWeightSlider from '../sliders/BikeWeightSlider';
import MaxAbsorptionSlider from '../sliders/MaxAbsorptionSlider';
import SafetyMarginSlider from '../sliders/SafetyMarginSlider';
import PlannerNavigation from './PlannerNavigation';

interface Props {
  route: RouteAnalysisResponse;
  initialOptions?: StrategyOptions;
  onComplete: (options: StrategyOptions, intervals: FuelIntervalResponse[], totalDuration: string) => void;
  onBack: () => void;
}

const ZONE_DESCRIPTIONS: Record<CyclingZone, string> = {
  Z1: 'Z1 - Recovery',
  Z2: 'Z2 - Endurance',
  Z3: 'Z3 - Tempo',
  Z4: 'Z4 - Threshold',
  Z5: 'Z5 - VO2max',
};

const ZONES: CyclingZone[] = ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'];

const ZONE_COLORS: Record<CyclingZone, string> = {
  Z1: '#4A4A4A',      // Dark gray
  Z2: '#2D5A8E',      // Muted blue
  Z3: '#2D8E5A',      // Emerald green
  Z4: '#B8860B',      // Dirty amber
  Z5: '#8E2D2D',      // Deep red
};

const STORAGE_KEY = 'broomwagon-settings';

function loadSettings() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

export default function StrategyOptionsForm({ route, initialOptions, onComplete, onBack }: Props) {
  const { user } = useUser();
  const { locale } = useLocaleContext();
  const s = loadSettings();
  const clerkMeta = user?.unsafeMetadata as { ftp?: number; weight?: number; bikeWeight?: number; maxAbsorption?: number; safetyMargin?: number } | undefined;
  const [profileExpanded, setProfileExpanded] = useState(false);

  const [ftpWatts, setFtpWatts] = useState<number>(
    initialOptions?.cyclist.ftpWatts ?? clerkMeta?.ftp ?? s.ftpWatts ?? 200
  );
  const [weightKg, setWeightKg] = useState<number>(
    initialOptions?.cyclist.weightKg ?? clerkMeta?.weight ?? s.weightKg ?? 75
  );
  const [bikeWeightKg, setBikeWeightKg] = useState<number>(
    initialOptions?.cyclist.bikeWeightKg ?? clerkMeta?.bikeWeight ?? s.bikeWeightKg ?? 8.0
  );
  const [targetZone, setTargetZone] = useState<CyclingZone>(initialOptions?.targetZone ?? s.targetZone ?? 'Z2');
  const [frequencyMinutes, setFrequencyMinutes] = useState<number>(initialOptions?.frequencyMinutes ?? s.frequencyMinutes ?? 20);
  const [maxAbsorptionRatePerHour, setMaxAbsorptionRatePerHour] = useState<number>(
    initialOptions?.maxAbsorptionRatePerHour ?? clerkMeta?.maxAbsorption ?? s.maxAbsorptionRatePerHour ?? 60
  );
  const [maxSurplusGramsPerInterval, setMaxSurplusGramsPerInterval] = useState<number>(
    initialOptions?.maxSurplusGramsPerInterval ?? clerkMeta?.safetyMargin ?? s.maxSurplusGramsPerInterval ?? 0
  );
  const maxDeficitGramsPerInterval = initialOptions?.maxDeficitGramsPerInterval ?? 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (clerkMeta?.ftp && !initialOptions?.cyclist.ftpWatts) {
      setFtpWatts(clerkMeta.ftp);
    }
    if (clerkMeta?.weight && !initialOptions?.cyclist.weightKg) {
      setWeightKg(clerkMeta.weight);
    }
  }, [clerkMeta?.ftp, clerkMeta?.weight, initialOptions?.cyclist.ftpWatts, initialOptions?.cyclist.weightKg]);

  useEffect(() => {
    try {
      const existing = loadSettings();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...existing,
        ftpWatts, weightKg, bikeWeightKg, targetZone,
        frequencyMinutes, maxAbsorptionRatePerHour,
        maxSurplusGramsPerInterval,
      }));
    } catch { /* quota exceeded */ }
  }, [ftpWatts, weightKg, bikeWeightKg, targetZone, frequencyMinutes,
      maxAbsorptionRatePerHour, maxSurplusGramsPerInterval]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const options: StrategyOptions = {
        cyclist: { ftpWatts, weightKg, bikeWeightKg },
        targetZone,
        frequencyMinutes,
        maxAbsorptionRatePerHour,
        maxSurplusGramsPerInterval,
        maxDeficitGramsPerInterval,
      };
      const result = await calculateFuelIntervals({
        route,
        cyclist: options.cyclist,
        targetZone,
        frequencyMinutes,
        maxAbsorptionRatePerHour,
        maxSurplusGramsPerInterval,
      });
      onComplete(options, result.intervals, result.totalDuration);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to calculate fuel intervals');
    } finally {
      setLoading(false);
    }
  }, [route, ftpWatts, weightKg, bikeWeightKg, targetZone, frequencyMinutes,
      maxAbsorptionRatePerHour, maxSurplusGramsPerInterval, maxDeficitGramsPerInterval, onComplete]);

  return (
    <div className="d-flex flex-column gap-3">
      <Card>
        <Card.Header className="d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-center">
            <Card.Title className="mb-0">{locale.planner.cyclistProfile}</Card.Title>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setProfileExpanded(p => !p)}
            >
              {profileExpanded ? (
                <>
                  {locale.planner.close} <span style={{ marginLeft: '0.5rem' }}>✕</span>
                </>
              ) : (
                locale.planner.edit
              )}
            </Button>
          </div>
          {!profileExpanded && (
            <span className="text-light" style={{ fontSize: '0.95rem' }}>
              <span className="fw-bold" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{locale.planner.cyclist.ftp}:</span> <span className="fw-bold">{ftpWatts}W</span> <span className="mx-2" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>•</span>
              <span className="fw-bold" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{locale.planner.cyclist.weight}:</span> <span className="fw-bold">{weightKg}kg</span> <span className="mx-2" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>•</span>
              <span className="fw-bold" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{locale.planner.cyclist.bike}:</span> <span className="fw-bold">{bikeWeightKg.toFixed(1)}kg</span> <span className="mx-2" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>•</span>
              <span className="fw-bold" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{locale.settings.cyclist.maxAbsorption}:</span> <span className="fw-bold">{maxAbsorptionRatePerHour}g/h</span> <span className="mx-2" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>•</span>
              <span className="fw-bold" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{locale.planner.safetyMargin}:</span> <span className="fw-bold">{maxSurplusGramsPerInterval}g</span>
            </span>
          )}
        </Card.Header>
        {profileExpanded && (
          <Card.Body className="d-flex flex-column gap-4">
            <div className="form-range-static">
              <FtpSlider
                value={ftpWatts}
                onChange={setFtpWatts}
                zoneColor={ZONE_COLORS[targetZone]}
              />
            </div>
            <div className="form-range-static">
              <WeightSlider
                value={weightKg}
                onChange={setWeightKg}
                zoneColor={ZONE_COLORS[targetZone]}
              />
            </div>
            <div className="form-range-static">
              <BikeWeightSlider
                value={bikeWeightKg}
                onChange={setBikeWeightKg}
                zoneColor={ZONE_COLORS[targetZone]}
              />
            </div>
            <div className="form-range-static">
              <MaxAbsorptionSlider
                value={maxAbsorptionRatePerHour}
                onChange={setMaxAbsorptionRatePerHour}
                zoneColor={ZONE_COLORS[targetZone]}
              />
            </div>
            <div className="form-range-static">
              <SafetyMarginSlider
                value={maxSurplusGramsPerInterval}
                onChange={setMaxSurplusGramsPerInterval}
                zoneColor={ZONE_COLORS[targetZone]}
              />
            </div>
          </Card.Body>
        )}
      </Card>

      <Card>
        <Card.Body className="strategy-options-section">
          <div className="d-flex flex-column gap-4">
            <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: '4px', padding: '1rem' }}>
              <div className="d-flex flex-column gap-3">
                <div>
                  <Form.Label className="text-light d-block mb-2">{locale.planner.targetZone}</Form.Label>
              <ButtonGroup>
                {ZONES.map((zone) => (
                  <Button
                    key={zone}
                    variant={targetZone === zone ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => setTargetZone(zone)}
                    className="zone-button"
                  >
                    {zone}
                  </Button>
                ))}
              </ButtonGroup>
              <div className="text-small mt-2" style={{ color: '#ffffff' }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: ZONE_COLORS[targetZone],
                    marginRight: '0.5rem',
                  }}
                />
                {ZONE_DESCRIPTIONS[targetZone]}
              </div>
            </div>

            <div>
              <Form.Label className="text-light d-block mb-2">{locale.planner.frequency}</Form.Label>
              <div className="text-secondary" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                {locale.planner.frequencyHint}
              </div>
              <ButtonGroup>
                {[15, 20, 30, 45, 60].map((freq) => (
                  <Button
                    key={freq}
                    variant={frequencyMinutes === freq ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => setFrequencyMinutes(freq)}
                    className="frequency-button"
                  >
                    {freq}
                  </Button>
                ))}
              </ButtonGroup>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <PlannerNavigation
        onBack={onBack}
        onNext={handleSubmit}
        loading={loading}
      />
    </div>
  );
}
